import { Link } from "react-router-dom";
import { SPORTS, fmtHeight, fmtStat, statLabel } from "@/constants/sports";
import { photoSrc } from "@/lib/api";
import { AttrIcon, ordinal } from "@/components/AttributesPanel";

const MatchChips = ({ p, keys }) => {
  const tags = (p.attributes_by_season?.[SPORTS[p.sport]?.attrSeason]?.tags || []).filter((t) => keys.includes(t.key))
    .sort((a, b) => (b.percentile ?? -1) - (a.percentile ?? -1));
  if (!tags.length) return null;
  return (
    <div data-testid={`card-attribute-chips-${p.id}`} className="mb-2 flex flex-wrap items-center gap-1.5">
      {tags.slice(0, 2).map((t) => (
        <span key={t.key} data-testid={`card-attribute-chip-${p.id}-${t.key}`}
          className="inline-flex items-center gap-1 rounded-full border border-[#FF2D78]/50 bg-[#0B0F19]/80 px-2 py-0.5 text-[11px] font-semibold text-[#FF2D78] backdrop-blur">
          <AttrIcon file={t.icon} sport={p.sport} className="h-3.5 w-3.5" />
          <span className="text-white">{t.label}{t.percentile != null ? ` · ${ordinal(t.percentile)}` : ""}</span>
        </span>
      ))}
      {tags.length > 2 && <span className="font-data text-[11px] text-slate-300">+{tags.length - 2}</span>}
    </div>
  );
};

const shortClass = (c) => c && c.replace("Freshman", "Fr.").replace("Sophomore", "So.").replace("Junior", "Jr.").replace("Senior", "Sr.").replace("Graduate", "Gr.").replace("Fifth Year", "5th");

export const CardFace = ({ p, matchKeys }) => {
  const sport = SPORTS[p.sport] || SPORTS.soccer;
  return (
    <>
      {photoSrc(p) ? (
        <img src={photoSrc(p)} alt={p.name} loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: `linear-gradient(160deg, ${p.team_color || "#1A2336"} 0%, #0B0F19 75%)` }}>
          {p.team_logo
            ? <img src={p.team_logo} alt="" className="w-3/5 -translate-y-8 object-contain opacity-30 grayscale-[30%] transition-transform duration-500 group-hover:scale-105" />
            : <span className="font-display text-8xl font-black text-white/10">{p.name?.[0]}</span>}
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/55 to-transparent" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.14)_0%,rgba(255,255,255,0)_45%)]" />
      <div className="absolute inset-x-0 top-0 h-1" style={{ background: sport.accent }} />
      <div className="absolute left-4 right-4 top-4 flex items-start justify-between">
        <span className="font-display text-4xl font-black leading-none text-white/90 drop-shadow">
          {p.jersey_number ? `#${p.jersey_number}` : ""}
        </span>
        <span className="rounded-full border border-white/20 bg-[#0B0F19]/70 px-2.5 py-1 font-data text-[10px] uppercase tracking-widest text-white backdrop-blur">
          {sport.label}
        </span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-4">
        {matchKeys?.length > 0 && <MatchChips p={p} keys={matchKeys} />}
        <p className="font-data text-[10px] uppercase tracking-[0.18em]" style={{ color: sport.accent }}>
          {(p.sport === "beach" ? [p.hometown] : [p.position, p.height_in && fmtHeight(p.height_in)]).filter(Boolean).join(" · ") || "\u00A0"}
        </p>
        <h3 className="mt-1 font-display text-3xl font-extrabold uppercase leading-[0.95] tracking-tight text-white">{p.name}</h3>
        <p className="mt-1 flex items-center gap-1.5 truncate text-sm text-slate-300">
          {p.team_logo && <img src={p.team_logo} alt="" className="h-4 w-4 shrink-0 object-contain" />}
          <span className="truncate">{p.team}{p.conference ? ` · ${p.conference}` : ""}</span>
        </p>
        <div className="mt-3 grid grid-cols-3 border-t border-white/10 pt-3">
          {!sport.keyStats.length && (p.sport === "beach" ? [["Ht", p.height_in && fmtHeight(p.height_in)], ["Class", shortClass(p.class_year)], ["Prev", p.previous_school?.split(" / ").pop()]] : [p.sport === "softball" ? ["B/T", p.bats_throws] : p.sport === "rugby" ? ["Unit", p.position_group !== "Unknown" && p.position_group] : ["Pos", p.position?.split("/").map((w) => w[0]).join("/")], ["Class", p.class_year?.replace("Sophomore", "Soph.").replace("Freshman", "Fr.")], ["Ht", p.height_in && fmtHeight(p.height_in)]]).map(([l, v]) => (
            <div key={l} className="text-center">
              <div className="truncate font-data text-sm font-bold text-white">{v || "—"}</div>
              <div className="font-data text-[9px] uppercase tracking-widest text-slate-400">{l}</div>
            </div>
          ))}
          {sport.keyStats.map((k) => (
            <div key={k} className="text-center">
              <div className="font-data text-lg font-bold text-white">{fmtStat(k, p.stats?.[k])}</div>
              <div className="font-data text-[9px] uppercase tracking-widest text-slate-400">{statLabel(p.sport, k)}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export const PlayerCard = ({ p, index = 0, matchKeys }) => (
  <Link
    to={`/players/${p.id}`}
    data-testid={`player-card-${p.id}`}
    style={{ animationDelay: `${Math.min(index, 12) * 50}ms` }}
    className="rise group relative block aspect-[3/4] overflow-hidden rounded-xl border border-slate-800 bg-[#131927] transition-[transform,box-shadow,border-color] duration-300 ease-out hover:-translate-y-2 hover:border-slate-600 hover:shadow-[0_20px_40px_rgba(255,45,120,0.22)]"
  >
    <CardFace p={p} matchKeys={matchKeys} />
  </Link>
);
